/**
 * 고객 자가 가입 (260916-client-self-signup).
 *
 * 공개 쓰기 경로다 — `status`·첫 소속은 **전부 서버가 고정**한다(본문에서 오지 않는다).
 * 가입은 세션을 발급하지 않는다. 승인 대기 상태로 만들어지고 운영자가 승인한다.
 */
import { z } from 'zod';
import { IdParams, IsoDateTime, Ok } from '../common.ts';
import { AssetLinks, BrandNames, Brand } from './registry.ts';
import { defineRoute } from '../route.ts';
import { SubscriptionOffer } from './billing.ts';
import { Password } from './auth.ts';

/** 약관·개인정보처리방침 판. 문서를 고치면 올린다 — 저장된 값과 다르면 재동의를 받을 수 있다 */
export const CONSENT_VERSION = '2026-10-15';

/**
 * 마케팅 정보 수신 동의(선택) 문안의 판. 약관·방침(CONSENT_VERSION)과 따로 센다 — 동의 문안만 고쳐도 어느 문안으로
 * 동의받았는지 증명할 수 있게. 문안(web/src/legal/content/marketing.ts)을 고치면 올린다 (260917-signup-marketing-consent)
 */
export const MARKETING_CONSENT_VERSION = '2026-09-17';

/** 휴대폰 — 하이픈은 있어도 없어도 된다. 저장은 숫자만 (normalizePhone) */
export const Phone = z.string().regex(/^(010-?\d{4}-?\d{4}|01[16789]-?\d{3,4}-?\d{4})$/, '휴대폰 번호 형식이 아니다');
export const Code = z.string().regex(/^\d{6}$/, '인증번호는 숫자 6자리다');

/**
 * 고객(브랜드)을 만들 때 받는 **원하는 질문 1개 + 브랜드명 1개** — 한 쌍이고 둘 다 필수다 (운영자 결정 2026-10-11).
 * 「이 질문을 AI 에 물었을 때 이 브랜드가 나와야 한다」가 측정의 단위라 짝으로 받는다.
 * ★ 질문은 **요청값**(`brand.desired_queries`)이고, 브랜드명은 브랜드의 **표시 이름**(`brand.name`)이 된다
 *   (261013-signup-company-name-to-account). 판정용 브랜드명(`brand_names`)은 운영자가 승인 때 이 값으로 시작해 확정한다 —
 *   표기 하나가 틀리면 언급률이 0 이 된다
 * (260917-signup-form-trim 의 「가입 화면에서 질문을 받지 않는다」를 뒤집는다)
 */
export const DesiredQuery = z
  .string()
  .trim()
  .min(2, '원하는 질문을 넣어 주세요')
  .max(300)
  .describe('AI 에 물었을 때 브랜드가 나와야 하는 질문');
export const BrandName = z
  .string()
  .trim()
  .min(1, '브랜드명을 넣어 주세요')
  .max(120)
  .describe('그 질문의 답변에 나와야 하는 브랜드명 — 간판·플레이스 표기. 브랜드의 표시 이름이 된다');

/** 저장은 배열이다(옛 가입은 여러 개를 받았다) — 지금은 1개만 들어온다 */
export const DESIRED_QUERIES_MAX = 3;

/** 이메일 인증 링크의 토큰 — 1회용. base64url */
export const LinkToken = z.string().min(20).max(200);

/** 발송 응답. **절대시각을 보내지 않는다** — 클라이언트 시계가 빠르면 즉시 만료로 보인다 */
const SendResult = z.object({
  expiresInMs: z.number().int(),
  resendAfterMs: z.number().int(),
  codeLength: z.number().int(),
});

/**
 * 인증 결과. `verifiedForMs` 는 **코드 만료가 아니라 "이 인증으로 신청할 수 있는 남은 시간"**이다.
 * 코드 TTL 과 분리한 이유 — inline 인증은 폼을 다 쓰기 전에 끝나므로, 코드 TTL 을 그대로 쓰면
 * "인증했는데 제출할 때 만료"가 상시 발생한다 (260916-signup-email-verification).
 */
const VerifyResult = z.object({ verifiedForMs: z.number().int() });

export const sendPhoneCode = defineRoute({
  method: 'post',
  path: '/api/signup/phone/code',
  access: 'public',
  summary: '가입용 휴대폰 확인번호 발송 — 번호·IP·전역 상한이 곧 비용 상한이다',
  body: z.object({ phone: Phone }).strict(),
  response: SendResult,
});

export const checkPhoneCode = defineRoute({
  method: 'post',
  path: '/api/signup/phone/check',
  access: 'public',
  summary: '휴대폰 인증 — 맞으면 인증으로 기록한다(소비하지는 않는다)',
  body: z.object({ phone: Phone, code: Code }).strict(),
  response: VerifyResult,
});

export const sendEmailCode = defineRoute({
  method: 'post',
  path: '/api/signup/email/code',
  access: 'public',
  summary: '가입용 이메일 인증번호 발송 — 메일에 코드와 인증 링크를 함께 싣는다',
  body: z.object({ email: z.email() }).strict(),
  response: SendResult,
});

export const checkEmailCode = defineRoute({
  method: 'post',
  path: '/api/signup/email/check',
  access: 'public',
  summary: '이메일 인증 — 맞으면 인증으로 기록한다(소비하지는 않는다)',
  body: z.object({ email: z.email(), code: Code }).strict(),
  response: VerifyResult,
});

export const verifyEmailLink = defineRoute({
  method: 'post',
  path: '/api/signup/email/verify-link',
  access: 'public',
  summary: '메일의 인증 링크 — 완료 페이지가 부른다. 토큰은 1회용',
  body: z.object({ token: LinkToken }).strict(),
  /** 어느 주소가 인증됐는지 **가려서** 보여준다 — 토큰만 쥔 사람에게 주소 전체를 알려주지 않는다 */
  response: z.object({ maskedEmail: z.string(), verifiedForMs: z.number().int() }),
});

export const emailVerifyStatus = defineRoute({
  method: 'post',
  path: '/api/signup/email/status',
  access: 'public',
  summary: '링크로 인증했는지 확인 — 신청서 탭이 보이는 동안 몇 초마다, 창으로 돌아올 때 바로 묻는다',
  body: z.object({ email: z.email() }).strict(),
  response: z.object({ verified: z.boolean(), verifiedForMs: z.number().int() }),
});

/** 가입 본문 — 화면 폼이 `.omit()` 으로 쓴다 */
export const SignupBody = z
  .object({
    email: z.email(),
    password: Password,
    /** 업체명(상호) — **계정** 회사명으로 남는다(`user_account.company_name`). 브랜드 이름이 아니다 */
    companyName: z.string().trim().min(1).max(120),
    /** 대표자명 */
    name: z.string().trim().min(1).max(60),
    phone: Phone,
    desiredQuery: DesiredQuery,
    /** 브랜드명 — 브랜드의 표시 이름(`brand.name`). 판정용 목표 브랜드명은 운영자가 정한다 */
    brandName: BrandName,
    /**
     * 원본 자산 — 홈페이지 **필수**(없으면 블로그 등 대표 주소) · 플레이스 권장 · 그 밖의 링크 자유 (261010-agency-console).
     * ★ 배포 순간 열려 있던 옛 탭은 이 칸이 없어 400 이 난다 — 다시 열면 된다(필수 항목이라 기본값으로 통과시키지 않는다)
     */
    ...AssetLinks,
    inquiry: z.string().trim().max(2000),
    agreeTerms: z.literal(true),
    agreePrivacy: z.literal(true),
    /** (선택) 마케팅 정보 수신 — 빠지면 미동의다(배포 순간 열려 있던 옛 탭도 통과한다) */
    agreeMarketing: z.boolean().default(false),
  })
  .strict();

export const submitSignup = defineRoute({
  method: 'post',
  path: '/api/signup',
  access: 'public',
  summary: '가입 신청 — 승인 대기 상태로 업체·계정을 만든다. 세션은 발급하지 않는다',
  /**
   * ★ **인증번호를 싣지 않는다.** 이메일은 링크로도 인증할 수 있어 폼에 코드가 없을 수 있다.
   *   서버가 "이 주소·번호에 최근 인증된 미소비 행이 있는가"로 판단한다 — 그 행은 해당 주소·번호로 간
   *   코드나 링크를 쥔 사람만 만들 수 있다.
   */
  body: SignupBody,
  response: z.object({ status: z.literal('pending') }),
});

export const SignupRow = z.object({
  brandId: z.uuid(),
  /** 브랜드 표시 이름 — 신청자가 넣은 브랜드명 */
  brandName: z.string(),
  name: z.string().nullable(),
  phone: z.string().nullable(),
  /** 신청한 계정(그 브랜드의 첫 소유자) */
  email: z.string().nullable(),
  /** 신청한 계정의 회사명(선택) — 브랜드 추가로 들어온 신청이면 그 계정이 누구인지 알아보게 */
  requesterCompanyName: z.string().nullable().default(null),
  homepageUrl: z.string().nullable().default(null),
  naverPlaceUrl: z.string().nullable().default(null),
  links: z.array(z.string()).default([]),
  desiredQueries: z.array(z.string()),
  inquiry: z.string().nullable(),
  /** (선택) 마케팅 정보 수신 동의 시각. null = 미동의 */
  marketingAgreedAt: IsoDateTime.nullable(),
  requestedAt: IsoDateTime,
});
export type SignupRow = z.infer<typeof SignupRow>;

export const listSignups = defineRoute({
  method: 'get',
  path: '/api/signups',
  access: 'admin',
  summary: '승인 대기 중인 신청 — 자가 가입과 브랜드 추가. 신청자 개인정보는 이 응답에만 실린다',
  response: z.object({ signups: z.array(SignupRow) }),
});

/**
 * 가입 승인 — **브랜드명을 확정하고 질문을 등록해야 승인된다** (운영자 결정 2026-10-11).
 * 신청자의 요청값(요청 브랜드명·원하는 질문)은 이 화면에서만 보이고, 운영자가 고쳐 확정한 값이 판정(brand_names)과
 * 수집(geo_query)에 들어간다. 셋(브랜드명 확정·질문 등록·승인)이 **한 트랜잭션**이다 — 반쪽 승인(브랜드명 없이 승인돼
 * 질문을 못 만드는 고객)이 생기지 않는다. 같은 문구의 질문이 이미 있으면 새로 만들지 않는다
 */
export const approveSignup = defineRoute({
  method: 'post',
  path: '/api/signups/{id}/approve',
  access: 'admin',
  summary: '가입 승인 — 확정 브랜드명·질문을 함께 받아 한 번에 등록하고 승인한다',
  params: IdParams,
  body: z
    .object({
      brandNames: BrandNames,
      query: z.string().trim().min(2, '질문을 넣어 주세요').max(300),
      samplesPerDay: z.number().int().min(0).max(48).default(10),
      /**
       * 구독 — **늘 만든다**(261015-subscription-unify). 질문은 이 승인에서 만드는 질문(`query`)이다(261013-subscription-query-link).
       * `startNow` 가 거짓이면 제안이다 — 브랜드는 **결제 대기(confirmed)** 가 되고 첫 결제가 성공해야 이용이 열린다.
       * 참이면 **결제 없이 바로 시작**한다(오프라인 청구·무료) — 브랜드는 바로 active, 구독은 카드 없이 이어진다
       */
      subscription: SubscriptionOffer.omit({ queryId: true }).extend({ startNow: z.boolean().default(false) }),
    })
    .strict(),
  response: z.object({ brand: Brand, warnings: z.array(z.string()) }),
});

export const rejectSignup = defineRoute({
  method: 'post',
  path: '/api/signups/{id}/reject',
  access: 'admin',
  summary: '가입 거절 — 업체·계정을 삭제(soft)한다. 같은 이메일로 다시 신청할 수 있다',
  params: IdParams,
  response: Ok,
});

export const withdraw = defineRoute({
  method: 'post',
  path: '/api/account/withdraw',
  access: 'session',
  summary: '탈퇴 — 개인정보를 파기하고 계정·세션을 없앤다. 측정 이력은 남는다',
  body: z.object({ password: z.string().min(1).max(200) }).strict(),
  response: Ok,
});
